import { createApp, reactive } from 'vue'
import { createPinia } from 'pinia'
import { configuration, setupAirbrake, dataLayer, privacy, authentication, api } from '@dpgradio/creative'
import { Notifier as AirbrakeNotifier } from '@airbrake/browser'
import './style.css'
import App from './App.vue'
import WelcomeView from './components/WelcomeView.vue'
import { usePollStore } from './stores/poll'

const isStaging = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has('staging')
}

const initialize = async () => {
  const parameters = new URLSearchParams(window.location.search)
  const stationId = parameters.get('stationId')

  if (!stationId) {
    const app = createApp(WelcomeView)
    app.mount('#app')
    return
  }

  try {
    await configuration.retrieveConfigForStation(stationId, 'signupMoments')
  } catch (error) {
    console.error(`
      Could not load configuration for station: ${stationId}

      ${error}
    `)
  }

  if (isStaging()) {
    const currentBaseUrl = api.baseUrl
    const stagingUrl = currentBaseUrl.replace('api.', 'api.stag.')
    api.baseUrlOverride = stagingUrl
  }

  // Set document language from config
  // const language = config('language')
  // if (language) {
  //   document.documentElement.lang = language
  // }

  authentication.initialize()

  const pinia = createPinia()
  const app = createApp(App)

  if (import.meta.env.PROD) {
    await setupAirbrake(
      AirbrakeNotifier,
      {
        projectId: '<project-id>',
        projectKey: '<project-key>',
        version: import.meta.env.VITE_COMMIT_HASH,
      },
      app
    )
  }

  app.use(pinia)

  const ratingId = parameters.get('ratingId')
  if (ratingId) {
    const pollStore = usePollStore()
    pollStore.setRatingId(ratingId)
  }

  const getTheme = (stationId) => {
    if (!stationId) return 'qmusic-theme'

    if (stationId.includes('joe')) return 'joe-theme'
    if (stationId.includes('willy')) return 'willy-theme'
    if (stationId.includes('qmusic')) return 'qmusic-theme'

    return 'qmusic-theme'
  }

  // I used to get these out of the styles with a getComputed, but
  // that sometimes ran into timing issues, so now I'm hardcoding them
  // here for speed and reliability. (They're the normal primary colors)
  const getPrimaryColor = (theme) => {
    const themeColors = {
      'joe-theme': '#DC3C96',
      'willy-theme': '#F7AE00',
      'qmusic-theme': '#ED1B24',
    }

    return themeColors[theme] || themeColors['qmusic-theme']
  }

  const theme = getTheme(stationId)

  app.provide('pollParams', reactive({
    title: parameters.get('title') || 'Poll',
    description: parameters.get('description') || '',
    emoji: parameters.get('slider') || '',
    image: parameters.get('image') || null,
    startLabel: parameters.get('min') || '',
    stopLabel: parameters.get('max') || '',
    mode: parameters.get('mode') || 'active',
    value: parameters.get('value') ? Number(parameters.get('value')) : null,
    theme: theme,
    primaryColor: getPrimaryColor(theme),
    isStaging: isStaging(),
    commitHash: import.meta.env.VITE_COMMIT_HASH,
  }))

  app.mount('#app')

  privacy.initialize()
  dataLayer.initialize()
  dataLayer.pushVirtualPageView()
}

initialize()
